<!-- File: IoT-User-Frontend/src/components/MaterialInput.vue 
  ------------------
  A reusable input component built with Bootstrap's styling classes and Material Design-inspired features.
  It supports:
    - Various input types (text, password, etc.)
    - Optional icon display
    - Dynamic classes for validation states (success/error)
    - Size control (sm, md, lg)
    - Props for disabled, required, and placeholder handling
    - Emits 'update:modelValue' for v-model support in parent components
-->
<script setup>
import { defineEmits } from 'vue';

const emit = defineEmits(['update:modelValue']);

defineProps({
  id: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  modelValue: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "md",
  },
  error: {
    type: Boolean,
    default: false,
  },
  success: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  inputClass: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
  },
});

function getClasses(size, success, error) {
  let sizeValue = size && `form-control-${size}`;
  let isValidValue = error
    ? "is-invalid"
    : success
      ? "is-valid"
      : "";

  return `${sizeValue} ${isValidValue}`;
}
</script>

<template>
  <div class="input-group mb-3">
    <span v-if="icon" class="input-group-text">
      <i class="fas" :class="`fa-${icon}`" aria-hidden="true"></i>
    </span>
    <input :id="id" :type="type" class="form-control" :class="[getClasses(size, success, error), inputClass]"
      :value="modelValue" @input="event => emit('update:modelValue', event.target.value)" :placeholder="placeholder"
      :required="required" :disabled="isDisabled" />
  </div>
</template>
